import axios from "axios";
import JSZip from "jszip";

const formHeaders = {
  'Content-Type': 'application/json',
  // "Content-Type": "multipart/form-data",
  "Access-Control-Allow-Origin": "*",
};

const urlPath = "visitSheet";

const saneNombreArchivo = (v) =>
  String(v ?? "")
    .replace(/[^a-zA-Z0-9_.-]/g, "_")
    .slice(0, 80) || "img";

// Zip mínimo que aceptan /import/:nifProp + unzipVisitBackup:
// form.json (campos como strings) + manifest.json + imgs/{principal,adjuntas}/*
// Ligero (sin raw-multipart ni meta de diagnóstico) porque es para el flujo normal
// de subida, no para el backup manual del técnico.
const construirZipEnvio = async (formData, mainImage, images) => {
  const zip = new JSZip();

  const form = {};
  for (const [k, v] of formData.entries()) {
    if (typeof v === "string") form[k] = v;
  }
  zip.file("form.json", JSON.stringify(form, null, 2));

  const manifest = { mainImage: null, images: [] };

  if (mainImage) {
    const nombre = saneNombreArchivo(mainImage.name || "principal.webp");
    const path = `imgs/principal/${nombre}`;
    zip.file(path, mainImage);
    manifest.mainImage = {
      path,
      name: mainImage.name || nombre,
      type: mainImage.type || "image/webp",
      size: mainImage.size ?? null,
    };
  }

  (images || []).forEach((file, i) => {
    if (!file) return;
    const nn = String(i).padStart(2, "0");
    const nombre = `${nn}-${saneNombreArchivo(file.name || `adjunta-${i}.webp`)}`;
    const path = `imgs/adjuntas/${nombre}`;
    zip.file(path, file);
    manifest.images.push({
      path,
      name: file.name || nombre,
      type: file.type || "image/webp",
      size: file.size ?? null,
    });
  });

  zip.file("manifest.json", JSON.stringify(manifest, null, 2));

  return zip.generateAsync({ type: "blob" });
};

// Decide si conviene reintentar con el zip: sin respuesta (red/abort de iOS) o 5xx.
const conviereFallbackZip = (res) => {
  if (!res) return true;
  const status = res?.status;
  return typeof status === "number" && status >= 500;
};

export const getVisitSheet = async (id) => {
  try {
    const response = await axios.get(`${urlPath}/${id}`);

    return response;
  } catch (error) {
    console.log(error);
  }
};

export const getUltimaVentaQR = async (token) => {
  try {
    const response = await axios.get(`/qr/obtener-ultima-venta/${token}`);

    return response;
  } catch (error) {
    console.log(error);
  }
};

export const getStatement = async () => {
  try {
    const response = await axios.get(`${urlPath}/statements`);

    try {
      if (Array.isArray(response.data) && response.data.length > 0) {
        localStorage.setItem("statementsCache", JSON.stringify(response.data));
      }
    } catch (e) {
      /* almacenamiento no disponible: se ignora */
    }

    return response.data;
  } catch (error) {
    console.log(error);
  }
};

// Construye EXACTAMENTE el mismo FormData que se envía al crear la hoja de visita.
// Se comparte entre createVisitSheet y la captura de diagnóstico para que el
// paquete de diagnóstico refleje byte a byte lo que se intenta subir.
export const construirFormDataVisita = (data, images, img) => {
  //Necesaria conversión para que el back lo reconozca con array
  const respuestas = Object.values(data.questions).map(preguntaPadre => {
    return {
        statements_id: preguntaPadre.statements_id,
        respuesta: preguntaPadre.respuesta,
        subquestions: preguntaPadre.subquestions
    };
  });

  const datos = {
    saleDate:data.fechaVisita,
    sale_id: data.sale_id,
    aaff_id:data.aaff_id,
    work_id:data.work_id,
    tech_id:data.IdTecnicoPRL,
    user_id:data.user_id,
    prop_id:data.IdComunidad,
    nPlantas:data.numeroPlantas,
    nVersion:1,
    portales: data.portales,
    questions: data.questions,
    comentario:data.comentarios,
    tipo_edificio: data.tipo_edificio,
    year:new Date().getFullYear()
  }

  const formData = new FormData();
  formData.append("sale_id", datos.sale_id);
  formData.append("aaff_id", datos.aaff_id);
  formData.append("prop_id", datos.prop_id);
  formData.append("work_id", datos.work_id);
  formData.append("tech_id", datos.tech_id);
  formData.append("nPlantas", datos.nPlantas);
  formData.append("comentario", datos.comentario);
  formData.append("nVersion", datos.nVersion);
  formData.append("saleDate", datos.saleDate);
  formData.append("year", datos.year);
  formData.append("tipo_edificio", datos.tipo_edificio);
  formData.append("questions", JSON.stringify(respuestas));

  formData.append("portales", JSON.stringify(datos.portales));
  formData.append("img", img);
  images.forEach((file) => {
    formData.append(`images`, file);
  });

  return { formData, datos, respuestas };
};

export const createVisitSheet = async (data, images, img, nifProp   ) => {

  const { formData, datos } = construirFormDataVisita(data, images, img);
  console.log(datos)

  let respMultipart;
  try {
    respMultipart = await axios.post(`${urlPath}/${nifProp}`, formData, {
      ...formHeaders,
    });
    return respMultipart;
  } catch (err) {
    // El interceptor puede rechazar con la PROPIA respuesta (err ya es la
    // respuesta, con .status/.data) o con un error de axios (err.response).
    if (err && typeof err.status !== "undefined") respMultipart = err;
    else if (err && err.response) respMultipart = err.response;
    else respMultipart = undefined;
  }

  // Fallback anti "Unexpected end of form": si el multipart se cortó (típico en
  // iOS Safari con múltiples imágenes o conexión inestable), reintentamos como
  // un único binario zip. El endpoint /import/:nifProp lo descomprime a
  // req.body/req.files y reutiliza el mismo validador + controlador.
  if (!conviereFallbackZip(respMultipart)) {
    return respMultipart;
  }

  try {
    const zipBlob = await construirZipEnvio(formData, img, images);
    const respZip = await importVisitSheetZip(zipBlob, nifProp);
    // Sólo devolvemos la del zip si terminó bien o con error de cliente
    // interpretable; si también es 5xx/red, mantenemos la del multipart original
    // (así el modal de reintento/backup ve el fallo real, no el del fallback).
    if (respZip && typeof respZip.status === "number" && respZip.status < 500) {
      return respZip;
    }
    return respMultipart;
  } catch (e) {
    return respMultipart;
  }
};


// Importa una hoja de visita subiendo su .zip de backup como un ÚNICO binario.
// El back lo descomprime y crea la visita (mismo validador/controlador que la
// creación normal). Al ser un solo stream, evita el error "Unexpected end of
// form" del multipart en conexiones inestables.
export const importVisitSheetZip = async (zipBlob, nifProp) => {
  try {
    const response = await axios.post(`${urlPath}/import/${nifProp}`, zipBlob, {
      headers: { "Content-Type": "application/zip" },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
    return response;
  } catch (err) {
    // Igual que createVisitSheet: normalizamos a la respuesta; sin ella, red.
    if (err && typeof err.status !== "undefined") return err;
    if (err && err.response) return err.response;
    return undefined;
  }
};

export const editarVisitSheet = async (data, imagesToDelete, images, img, nifProp   ) => {
  const id = data.visitSheet_id;
  const respuestas = Object.values(data.questions).map(preguntaPadre => {
    return {
        id: preguntaPadre.id,
        statements_id: preguntaPadre.statements_id,
        respuesta: preguntaPadre.respuesta,
        subquestions: preguntaPadre.subquestions
    };
  
});
  const datos = {
    saleDate:data.fechaVisita,
    visitSheet_id: data.visitSheet_id,
    sale_id: data.sale_id,
    aaff_id:data.aaff_id,
    // work_id:1,
    user_id:data.user_id,
    tech_id:data.IdTecnicoPRL,
    // prop_id:data.IdComunidad,
    nPlantas:data.numeroPlantas,
    nVersion:1,
    questions:respuestas,
    portales: data.portales,
    delPortales: data.delPortales,
    comentario:data.comentario,
    tipo_edificio: data.tipo_edificio,
    year:new Date().getFullYear()
  }
  
  const json = JSON.stringify(datos);

  const formData = new FormData();
  formData.append("data", json);
  
  
  formData.append("img", img);
  images.forEach((file, index) => {
    formData.append(`images-[${index}]`, file);
  });

  
  imagesToDelete.forEach((image, index) => {
    if (image && typeof image === 'object' && 'id' in image && 'path' in image) {
      formData.append(`images_to_delete[${index}][id]`, image.id);
      formData.append(`images_to_delete[${index}][path]`, image.path);
    }
  });

  try {
    
    const response = await axios.put(`${urlPath}/${nifProp}/${id}`, formData, {
      ...formHeaders,
    });
    return response;
  } catch (err) {
    if (err && typeof err.status !== "undefined") return err;
    if (err && err.response) return err.response;
    return undefined;
  }
};

export const visitTec = async (data) => {
  try {
    const response = await axios.post(`${urlPath}/visitTec`, data);
    return response.data;
  } catch (error) {
    console.log(error);
  }
}